import React, {
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback
} from "react";
import ApolloContext from "@tvg/utils/apolloContext";
import ApolloContextClients from "@tvg/utils/types/ApolloContext";
// Store
import {
  getBetGroupStatus,
  getErrorBets,
  getProcessingBets,
  getSuccessBets
} from "@tvg/desktop-bet/src/store/selectors";
import { useSelector } from "react-redux";
// DS
import { BetTypeCell, Breed } from "@tvg/design-system";
import {
  Mtp,
  Button,
  Paragraph,
  Header,
  Modal,
  AlertInLine,
  LoadingSpinner
} from "@tvg/design-system/web";
// Types
import { BetBasicInfo, BetRepeatInfo } from "@tvg/ts-types/Bet";
import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import { BetStatus, BetStatusType } from "@tvg/desktop-bet/src/types";
// Utils
import { get } from "lodash";
import BetUtils from "@tvg/utils/betSelection";
import formatCurrency from "@tvg/formatter/currency";
import { getCurrentProbableValue } from "@tvg/sh-lib-my-bets/utils/potentialReturn";
import { RaceProgram, RaceStatusEnum } from "@tvg/ts-types/Race";
import { NullaryFn, UnaryFn } from "@tvg/ts-types/Functional";
import { isRaceOff } from "@tvg/sh-lib-my-bets/utils/raceDetails";
import calculateTotals from "@tvg/api/wtx/BetHelper";
import { getBettingInfo } from "@tvg/desktop-bet/src/utils/betUtils";
import { getAccountNumber, getHasPreviousWagered } from "@urp/store-selectors";
import useGeocomplyBetDisabled from "@urp/betslip/src/hooks/useGeocomplyBetDisabled";
import { isFDR, pluralize } from "@tvg/utils/generalUtils";
import { isXSell } from "@tvg/sh-utils/mobileUtils";
import { replaceCAPIVariables } from "@tvg/utils/capiUtils";
import { getSelectionsRunnerNbr } from "../../utils/raceUtils";
// Hooks
import { useBetBlockedByState, usePlaceBet } from "../../hooks";
import { useCancelLimits } from "../ConfirmBetModal/hooks/useCancelLimits";
// Components
import {
  RepeatBetContainer,
  BetSumaryContainer,
  ButtonsContainer,
  DummyElement
} from "./styled-components";
import RepeaterSelector from "./components/RepeaterSelector";
import Alerts from "./components/Alerts";
import { AlertMessageContainer } from "../ConfirmBetModal/styled-components";
// Relative path Store
import {
  getBonus,
  getBonusFundsWagerMessages,
  getBonusFundsFeedbackEnabled,
  getBetConfirmationMessages,
  getBetConfirmModalTexts,
  getCancelLimitBetSlipEnabled,
  getCancelLimitsBetSlipConf,
  getEnableUrpConfirmModalRepeatBet,
  getRepeatBetConfig,
  getUserBalanceIsShown,
  getEnableURPAlternates
} from "../../store/selectors";
import onRepeatBetSuccessfullyEvent from "./utils/gtm/onRepeatBetSuccesfully";
import { confirmBetGtmEvent } from "../../utils/gtm/betConfirmJourney";
import RepeatBetModalHeader from "./components/RepeatBetModalHeader";
import { getSpecialWagerType } from "../../utils/wagersTypes";

interface RepeatBetModalProps {
  isRepeatBetModalOpen: boolean;
  race: RaceProgram;
  races: RaceProgram[];
  raceWager: BetBasicInfo | BetRepeatInfo;
  userBalance: number;
  setRepeatBetModalOpen: UnaryFn<boolean, void>;
  onResponsibleGamingError?: NullaryFn<void>;
  closeModalCallback?: NullaryFn<void>;
  isSpecialWager?: boolean;
}

const RepeatBetModal: React.FC<RepeatBetModalProps> = ({
  isRepeatBetModalOpen,
  race,
  races,
  raceWager,
  userBalance,
  setRepeatBetModalOpen,
  onResponsibleGamingError,
  closeModalCallback,
  isSpecialWager
}) => {
  const { behgClient } = useContext<ApolloContextClients>(ApolloContext);
  const [isWagerableState, setWagerableState] = useState(true);
  const [isInvalidBet, setIsInvalidBet] = useState(false);
  const [isLoadingBonus, setIsLoadingBonus] = useState(true);
  const [hasBonusBeforeBet, setHasBonusBeforeBet] = useState(false);

  const {
    handleSubmitBet,
    resetBetting,
    errorMessages,
    betDisable,
    clearErrorMessages
  } = usePlaceBet();

  if (isRaceOff(race.status?.code || RaceStatusEnum.OPEN)) {
    setRepeatBetModalOpen(false);
  }

  const MAX_REPETITIONS = 20;
  const [selectedRepetition, setSelectedRepetition] = useState(1);
  let typeFromRace = BetUtils.getOriginalBetTypeFromRace(
    race,
    raceWager.wagerType.id as unknown as WagerTypeCodesEnum
  );

  // ====== hack because program page way of identify WPS ======
  // Some functions like bet amount calculation it uses the columnCount to identify the type of calculation required to be done,
  // and for example Win/Place/Show in those functions required the columnCount === 1 to enter in the correct formula.
  // but some other fuctions like how the buttons should appear in the programPage required this to have the correct value of 3 (Ex: WPS bet type)
  // so here we are disconnecting the object from the one used in the parents components so we can change the value withotu interfering in anything.
  typeFromRace = typeFromRace?.specialGroup
    ? { ...typeFromRace, columnCount: 1 }
    : typeFromRace;
  const [isBlockedByState, blockedByStateErrorMessage] = useBetBlockedByState();
  const { isBetDisabled: isGeoComplyBetDisabled } = useGeocomplyBetDisabled();
  const isPlacingBetError = useSelector(getErrorBets);
  const betGroupStatus = useSelector(getBetGroupStatus);
  const betConfirmationMessages = useSelector(getBetConfirmationMessages);
  const confirmModalMessages = useSelector(getBetConfirmModalTexts);
  const cancelLimitConfig = useSelector(getCancelLimitsBetSlipConf);
  const cancelLimitFeedbackEnabled = useSelector(getCancelLimitBetSlipEnabled);
  const accountNumber = useSelector(getAccountNumber);
  const repeatBetConfig = useSelector(getRepeatBetConfig);
  const userBalanceIsShown = useSelector(getUserBalanceIsShown);
  const isPlacingBetSuccess = useSelector(getSuccessBets);
  const isPlacingBet = useSelector(getProcessingBets);
  const selectionsRunnerNbr = getSelectionsRunnerNbr(raceWager);
  const enableUrpConfirmModalRepeatBet = useSelector(
    getEnableUrpConfirmModalRepeatBet
  );
  const userBonus = useSelector(getBonus);
  const hasMadePreviousWager = useSelector(getHasPreviousWagered);
  const bonusFundsWagerMessages = useSelector(getBonusFundsWagerMessages);
  const isBonusFundsFeedbackEnabled = useSelector(getBonusFundsFeedbackEnabled);
  const isAlternatesToggleOn = useSelector(getEnableURPAlternates);

  const { numberOfWagerableRunners, fullSelections } = getBettingInfo(
    selectionsRunnerNbr,
    raceWager.wagerAmount,
    race,
    races,
    typeFromRace,
    isAlternatesToggleOn
  );
  const scratches = BetUtils.getSelectionsRunnerNbrScratched(fullSelections);
  const handleNrOfScratches = Object.values(scratches).filter(
    (value) => value.length
  );
  const placeBetError =
    isPlacingBetError > 0 && isPlacingBet === 0 && errorMessages.length > 0;
  const isLoading = isPlacingBet > 0;
  const placeBetSuccess =
    isPlacingBetSuccess > 0 && isPlacingBet === 0 && !placeBetError;

  const type = get(typeFromRace, "type.code", WagerTypeCodesEnum.WIN);
  const mtp = get(race, "mtp", "0");
  const status = get(race, "status.code");
  const postTime = get(race, "postTime");
  const trackName = get(race, "track.trackName");
  const betRaceNumber = get(race, "raceNumber");
  const trackCode = get(race, "track.trackCode");
  const trackCountry = get(race, "track.trackLocation.country");
  const isRaceWagerable = get(race, "wagerable", false);

  useEffect(() => {
    clearErrorMessages();
  }, [selectedRepetition]);

  useEffect(() => {
    const wagerableState =
      status !== RaceStatusEnum.RACE_OFF &&
      status !== RaceStatusEnum.RACE_OFFICIAL &&
      status !== "C";
    setWagerableState(wagerableState);
  }, [status]);

  useEffect(() => {
    const isBetInvalid = fullSelections.some((selection) =>
      selection.every((runner) => runner.scratched)
    );
    if (isBetInvalid) {
      setIsInvalidBet(true);
    }
  }, [JSON.stringify(scratches), JSON.stringify(fullSelections)]);

  useEffect(() => {
    if (isRepeatBetModalOpen) clearErrorMessages();
  }, [isRepeatBetModalOpen]);

  useEffect(() => {
    if (userBonus) {
      setHasBonusBeforeBet(true);
    }
    if (placeBetSuccess) {
      setIsLoadingBonus(false);
    }
  }, [userBonus]);

  const approximatedPayout = useMemo(
    () =>
      getCurrentProbableValue(
        get(race, "probables", []),
        get(raceWager, "wagerType"),
        get(raceWager, "wagerAmount", 0),
        selectionsRunnerNbr
      ),
    [race, raceWager, selectedRepetition]
  );

  const handleCloseModal = () => {
    setRepeatBetModalOpen(false);
    setSelectedRepetition(1);
    clearErrorMessages();
    if (closeModalCallback) closeModalCallback();
  };

  const completeSelections = useCallback(() => {
    const validSelections = fullSelections.map((selection) =>
      selection.filter((obj) => !obj.scratched)
    );

    const validSelectionsWithoutAlternates = validSelections.map(
      (validSelection) => validSelection.filter((obj) => !obj.isAlternate)
    );

    const fullSelectionsWithoutAlternates = fullSelections.map(
      (fullSelection) => fullSelection.filter((obj) => !obj.isAlternate)
    );

    if (!isAlternatesToggleOn) {
      return handleNrOfScratches.length
        ? validSelectionsWithoutAlternates
        : fullSelectionsWithoutAlternates;
    }

    return handleNrOfScratches.length ? validSelections : fullSelections;
  }, [fullSelections, isAlternatesToggleOn, handleNrOfScratches]);

  const handleRepeatBet = useCallback(() => {
    const specialWagerType = getSpecialWagerType(race);

    confirmBetGtmEvent({
      betAmount: +raceWager.wagerAmount,
      betType: raceWager.wagerType.name,
      raceType: race.type?.name,
      specialWagerType
    });
    handleSubmitBet({
      betAmount: +raceWager.wagerAmount,
      betSelectionsGroup: completeSelections(),
      race,
      selectedRepetition,
      forceWagerType: get(typeFromRace, "type.id"),
      hasMadePreviousWager
    });
    onRepeatBetSuccessfullyEvent({
      betAmount: +raceWager.wagerAmount,
      betType: raceWager.wagerType.name,
      raceType: race.type?.name
    });
  }, [
    race,
    raceWager,
    selectedRepetition,
    completeSelections,
    typeFromRace,
    hasMadePreviousWager,
    handleSubmitBet,
    onRepeatBetSuccessfullyEvent,
    confirmBetGtmEvent
  ]);

  const handleSelections = useCallback(
    () =>
      Object.values(completeSelections()).map((arr) =>
        arr
          .filter((selection) => !selection.isAlternate) // Exclude alternate runners
          .map((selection) => Number(selection.number.replace(/\D/g, "")))
      ),
    [completeSelections]
  );

  // filter repeated if array has coupled runners
  const uniqueSelectionsHandled = (handledSelections: Array<Array<number>>) =>
    handledSelections.map((sel: Array<number>) =>
      sel.filter((item, index) => sel.indexOf(item) === index)
    );

  const totals = typeFromRace
    ? calculateTotals(
        raceWager.wagerAmount,
        typeFromRace,
        uniqueSelectionsHandled(handleSelections())
      )
    : 0;

  const totalAmount =
    selectedRepetition *
    (typeof totals === "object" ? +totals.betCost : totals);

  const { feedback } = useCancelLimits({
    needsFetch: isRepeatBetModalOpen && cancelLimitFeedbackEnabled,
    accountNumber,
    behgClient,
    config: cancelLimitConfig,
    feedbackExtraValues: {
      forceFeedback: placeBetSuccess ? "placed" : undefined,
      prediction: {
        amount: typeof totals === "object" ? +totals.betCost : totals,
        count: selectedRepetition
      },
      track: {
        code: trackCode,
        name: trackName,
        country: trackCountry
      }
    },
    currentRace: trackCode
  });

  const isWagerableRace = isXSell() ? isWagerableState : isRaceWagerable;

  const placeBetNotAllowed =
    isBlockedByState ||
    betDisable ||
    isGeoComplyBetDisabled ||
    userBalance < totalAmount ||
    !isWagerableRace ||
    placeBetSuccess;

  const betsStatus = Object.values<BetStatus>(betGroupStatus).map(
    (betType) => betType && betType.status
  );

  const showRaceOffAlert =
    (status === RaceStatusEnum.RACE_OFF &&
      Object.keys(betGroupStatus).length !== 0 &&
      betsStatus.includes(BetStatusType.ERROR)) ||
    (status === RaceStatusEnum.RACE_OFF &&
      Object.keys(betGroupStatus).length === 0);

  const isBetRepeatable = useMemo(
    () =>
      placeBetSuccess &&
      !fullSelections.some((entry) => entry.some((value) => value.scratched)),
    [placeBetSuccess, fullSelections]
  );

  const isResponsibleGamingError = !!(
    errorMessages?.some(({ message }) => message.buttonType === "rgLimits") &&
    isFDR() &&
    onResponsibleGamingError
  );

  const bonusMessage = () => {
    if (placeBetSuccess) {
      const splitMessage =
        bonusFundsWagerMessages?.betPlaced.split("#bonusValue#");
      return (
        <AlertMessageContainer>
          {splitMessage[0]}
          {isLoadingBonus ? <LoadingSpinner /> : formatCurrency(userBonus)}
          {splitMessage[1]}
        </AlertMessageContainer>
      );
    }

    return replaceCAPIVariables(bonusFundsWagerMessages?.confirmBet, {
      bonusValue:
        +userBonus >= +totalAmount
          ? formatCurrency(+totalAmount)
          : formatCurrency(userBonus)
    });
  };

  return (
    <Modal
      isOpen={isRepeatBetModalOpen}
      headerProps={{
        title: placeBetSuccess
          ? `${pluralize(selectedRepetition, "Bet", true, "s", "x")} Placed`
          : "Repeat Bet",
        description: +userBalanceIsShown
          ? `BALANCE ${formatCurrency(userBalance)}`
          : "",
        showCloseButton: true
      }}
      padding={0}
      onClose={handleCloseModal}
      onAfterClose={() => resetBetting(false)}
      qaLabel="repeat-bet-modal"
      customHeader={
        <RepeatBetModalHeader
          userBalance={userBalance}
          placeBetSuccess={placeBetSuccess}
          repetitions={selectedRepetition}
          showBalance={Boolean(+userBalanceIsShown)}
          onClose={handleCloseModal}
        />
      }
      hideHeader
    >
      <RepeatBetContainer>
        <Alerts
          isRaceWagerable={isRaceWagerable}
          showRaceOffAlert={showRaceOffAlert}
          hasScratches={handleNrOfScratches.length > 0}
          errorMessages={errorMessages}
          placeBetSuccess={placeBetSuccess}
          placeBetError={placeBetError}
          isBlockedByState={isBlockedByState}
          userBalance={userBalance}
          totalAmount={totalAmount}
          blockedByStateErrorMessage={blockedByStateErrorMessage}
          feedback={feedback}
          betConfirmationMessages={betConfirmationMessages}
          confirmModalMessages={confirmModalMessages}
        />
        {isBonusFundsFeedbackEnabled &&
        ((!placeBetSuccess && userBonus) ||
          (placeBetSuccess && hasBonusBeforeBet)) ? (
          <AlertInLine
            type="fixed"
            qaLabel="bonusConfirmUse"
            message=""
            variant="success"
          >
            {bonusMessage()}
          </AlertInLine>
        ) : null}
        <BetSumaryContainer>
          <Mtp
            mtp={+mtp}
            status={status}
            postTime={postTime as string}
            qaLabel="confirm-bet-modal-mtp"
            size="xs"
          />
          <Header
            tag="h3"
            fontFamily="bold"
            color="grey.900"
            qaLabel="confirm-bet-modal-race-name"
            mb="space-1"
          >
            {`${trackName} ${isSpecialWager ? "" : "R"}${!isSpecialWager ? betRaceNumber : ""}`}
          </Header>
          <BetTypeCell
            mt={24}
            qaLabel="rbm-bet-cell"
            completeSelection={fullSelections}
            selections={get(fullSelections, 0)}
            scratches={scratches}
            approximatedPayout={approximatedPayout}
            numWagerableRunners={numberOfWagerableRunners}
            betAmount={formatCurrency(raceWager.wagerAmount)}
            betTicket={formatCurrency(
              typeof totals === "object" ? +totals.betCost : totals
            )}
            type={type}
            breed={
              get(race, "type.name", "Thoroughbred").toLowerCase() as Breed
            }
            isKey={get(typeFromRace, "isKey", false)}
            isBox={get(typeFromRace, "isBox", false)}
            isWheel={get(typeFromRace, "isWheel", false)}
            betStatus="NONE"
            betTypeName={get(typeFromRace, "type.name", WagerTypeCodesEnum.WIN)}
            isLeg={get(typeFromRace, "legCount", 1) > 1}
            raceNumber={betRaceNumber}
            isRepeatable={isBetRepeatable && enableUrpConfirmModalRepeatBet}
            onRepeatBetHandler={() => resetBetting()}
            displayAlternateSelection={isAlternatesToggleOn}
            showAlternatePlaceholder={false}
            isSpecialWager={isSpecialWager}
          />
          {!placeBetSuccess && !isInvalidBet && (
            <RepeaterSelector
              isDisabled={isLoading || placeBetSuccess}
              repetitions={repeatBetConfig.maxAllowed || MAX_REPETITIONS}
              selectedRepetition={selectedRepetition}
              setSelectedRepetition={setSelectedRepetition}
            />
          )}
          <DummyElement />
        </BetSumaryContainer>
        <ButtonsContainer>
          {!placeBetSuccess && !isInvalidBet && (
            <>
              {isResponsibleGamingError ? (
                <Button
                  variant="primary"
                  onClick={onResponsibleGamingError}
                  qaLabel="betConfirmation-rgLimitsBtn"
                  mt="space-7"
                  isStretched
                >
                  View Limits
                </Button>
              ) : (
                <Button
                  variant="betting"
                  onClick={handleRepeatBet}
                  qaLabel="confirm-bet-modal-sucess-button"
                  mt="space-7"
                  isDisabled={isLoading || placeBetNotAllowed}
                  isLoading={isLoading}
                  isStretched
                >
                  <Paragraph
                    qaLabel=""
                    fontFamily="medium"
                    color={
                      placeBetNotAllowed
                        ? "--fd-colors-component-button-primary-content-disabled"
                        : "--fd-colors-component-button-primary-content-base"
                    }
                  >
                    <>
                      Confirm x{selectedRepetition} Repeat |{" "}
                      {formatCurrency(totalAmount)} Bet
                    </>
                  </Paragraph>
                </Button>
              )}
            </>
          )}
          <Button
            variant={placeBetSuccess ? "primary" : "secondary"}
            onClick={handleCloseModal}
            qaLabel="confirm-bet-modal-close-button"
            mt="space-4"
            isStretched
          >
            Close
          </Button>
        </ButtonsContainer>
      </RepeatBetContainer>
    </Modal>
  );
};

export default RepeatBetModal;
